import React, { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from "react";
import { Row, Col, Button } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import BreadcrumbLink from "../../../components/Common/BreadcrumbLink";
import AlertSuccess from "../../../components/Alerts/AlertSuccess";
import LoaderRombo from "../../../components/Common/LoaderRombo";
import logoBlack from "../../../assets/images/logo-black.png";
import i18n from "../../../i18n";
import { ServiceStatus } from "../../../constants/enums";
import { getAddressInfo, minimumLoadingTime, orgId } from "../../../constants/utils";
import { getServiceAdmin, getServiceRequestAdmin, getTripsTimeline, getTripsResume } from "../../../helpers/api_rombo_helper";
import { GeneralInfo, ServiceDetails, Passengers, Addresses, Actions, makeGetEventDetails, formatServiceDateTime } from "./utils";

const DetailService = ({ serviceCode, serviceStatus, isPopup, onClose }) => {
  document.title = `${i18n.t("ServiceDetail")} - ${orgId}`;

  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [textHeader, setTextHeader] = useState("");
  const [textBody, setTextBody] = useState("");
  const [infoUser, setInfoUser] = useState({ permissions: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [timelineData, setTimelineData] = useState([]);
  const [gpxData, setGpxData] = useState(null);
  const [service, setService] = useState({
    scheduled_start: {},
    stops: [],
    riders: [],
    url_image: "",
  });

  const tabs = useMemo(
    () => [
      { key: "general", label: i18n.t("General") },
      { key: "details", label: i18n.t("ServiceDetail") },
      { key: "passengers", label: i18n.t("Passengers") },
      { key: "addresses", label: i18n.t("Addresses") },
      { key: "actions", label: i18n.t("Actions") },
    ],
    []
  );

  const tabRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const updateIndicator = useCallback(() => {
    const el = tabRefs.current[activeTab];
    if (el) {
      setIndicatorStyle({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [activeTab]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator, isLoading]);

  useEffect(() => {
    window.addEventListener("resize", updateIndicator);
    return () => window.removeEventListener("resize", updateIndicator);
  }, [updateIndicator]);

  const actualServiceCode = serviceCode || id;

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({
      token: Login.token,
      loading: Login.loading,
      data: Login.data,
    })
  );

  const { token, loading, data } = useSelector(LoginProperties);

  useEffect(() => {
    const startTime = Date.now();
    const fetchServices = async () => {
      let useReservationsEndpoint = false;

      if (isPopup) {
        useReservationsEndpoint = serviceStatus && serviceStatus !== ServiceStatus.UNASSIGNED;
      } else {
        useReservationsEndpoint =
          location.pathname.includes("/services/reservations") ||
          location.pathname.includes("/services/assignment") ||
          location.pathname.includes("/services/historical");
      }

      if (useReservationsEndpoint) {
        return getServiceAdmin(token, actualServiceCode, {});
      } else {
        return getServiceRequestAdmin(token, actualServiceCode, {});
      }
    };

    fetchServices()
      .then(async (resp) => {
        setService(resp);
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(minimumLoadingTime - elapsedTime, 0);

        try {
          const timelineResponse = await getTripsTimeline(token, { booking_code: resp.code });
          if (timelineResponse && timelineResponse.trip) {
            const flatTimeline = timelineResponse.trip.map(obj => Object.values(obj)[0]);
            setTimelineData(flatTimeline);
          }
        } catch (timelineError) {
          console.error("Error getting timeline:", timelineError);
        }
  
        setTimeout(() => setIsLoading(false), remainingTime);
      })
      .catch((error) => {
        console.error(`Error - ${error}`);
        setIsLoading(false);
      });
  }, [loading, location.pathname, token, actualServiceCode, isPopup]);  

  const { origin, destination, scales } = useMemo(() => getAddressInfo(service), [service]);

  const getGpxBaseUrl = () => {
    const env = import.meta.env.VITE_APP_DEFAULTAUTH;
    return env === 'dev' 
      ? 'https://s3.us-west-1.amazonaws.com/app-pt-trips.stg' 
      : 'https://s3.us-west-1.amazonaws.com/app-pt-trips.prod';
  };

  useEffect(() => {
    if (!service?.code || service?.status !== ServiceStatus.FINISHED) return;
  
    const fetchTripsResume = async () => {
      try {
        const resumeResponse = await getTripsResume(service.code, 'gpx');
        
        if (resumeResponse && resumeResponse.url) {
          const response = await fetch(resumeResponse.url, { method: 'HEAD' });
          if (response.ok) {
            setGpxData(resumeResponse.url);
            return;
          }
        }
        
        await searchInS3();
        
      } catch (error) {
        if (error.response?.status === 409 || 
            (error.message && error.message.includes('409'))) {
          await searchInS3();
        } else {
          console.error("Failed to fetch trip summary:", {
            error: error.message,
            status: error.response?.status,
          });
          setGpxData(null);
        }
      }
    };
  
    const searchInS3 = async () => {
      try {
        const normalizedCode = service.code.toLowerCase().replace(/-/g, '_').replace(/^tp_/, '');
        const gpxBaseUrl = getGpxBaseUrl();
        const gpxUrl = `${gpxBaseUrl}/tp_${normalizedCode}.gpx`;
        
        const response = await fetch(gpxUrl, { method: 'HEAD' });
        if (response.ok) {
          setGpxData(gpxUrl);
        } else {
          console.warn("GPX file not found in S3");
          setGpxData(null);
        }
      } catch (s3Error) {
        console.error("Error searching in S3:", s3Error);
        setGpxData(null);
      }
    };
  
    fetchTripsResume();
  }, [service.code, service?.status]);  

  // Lógica del timeline extraída a ./utils (compartida con el detalle de
  // validación de servicios). getEventDetails depende del `service` actual.
  const getEventDetails = makeGetEventDetails(service);
  const formatDateTime = formatServiceDateTime;

  return (
    <div className="page-content">
      <div className="container-fluid detail-service-page">
        <BreadcrumbLink title={i18n.t("Services")} link="/services/request" breadcrumbItem={i18n.t("ServiceDetail")} />
        <Row className="p-2 bg-white detail-service-header">
          <Col xxl={6} md={6} xl={6} xs={12} className="d-flex">
            <img src={logoBlack} alt="logo" className="detail-service-logo" height="120" />
          </Col>
          <Col xxl={6} md={6} xl={6} xs={12} className="detail-service-code d-flex justify-content-end align-items-start">
            {`${i18n.t("Service")} # ${service.code} - ${i18n.t(`${service.type}`)}`}
          </Col>
          <div className="separator" />
        </Row>
        {isLoading ? (
          <Row className="p-2 bg-white d-flex justify-content-center align-items-center">
            <LoaderRombo />
          </Row>
        ) : (
          <>
            <div className="bg-white service-tabs">
              <div className="service-tabs-list" role="tablist">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    ref={(el) => (tabRefs.current[tab.key] = el)}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`service-tab ${activeTab === tab.key ? "active" : ""}`}
                  >
                    {tab.label}
                  </button>
                ))}
                <span
                  className="service-tab-indicator"
                  style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
                />
              </div>
            </div>
            <Row className="p-4 bg-white">
              {activeTab === "general" && <GeneralInfo service={service} />}
              {activeTab === "details" && (
                <ServiceDetails
                  service={service}
                  onNoteUpdated={(newMessage) =>
                    setService((prev) => ({
                      ...prev,
                      info: {
                        ...prev.info,
                        notes: [{ ...(prev.info?.notes?.[0] || {}), message: newMessage }],
                      },
                    }))
                  }
                />
              )}
              {activeTab === "passengers" && <Passengers service={service} />}
              {activeTab === "addresses" && (
                <Addresses 
                  service={service} 
                  origin={origin} 
                  destination={destination} 
                  scales={scales}
                  gpxData={gpxData}
                />
              )}
              {activeTab === "actions" && (
                <Actions 
                  service={service} 
                  timelineData={timelineData}
                  getEventDetails={getEventDetails}
                  formatDateTime={formatDateTime}
                />
              )}
            </Row>
            {!isPopup && (
              <Row className="p-2 bg-white">
                <Col className="text-end">
                  <button
                    onClick={() => navigate(-1)}
                    className="btn btn-go-back mx-2"
                  >
                    {i18n.t("GoBack")}
                  </button>
                </Col>
              </Row>
            )}
          </>
        )}
        <AlertSuccess
          showAlertSuccess={showAlertSuccess}
          setShowAlertSuccess={setShowAlertSuccess}
          textHeader={textHeader}
          textBody={textBody}
        />
      </div>
    </div>
  );
};

export default DetailService;
